import React from 'react'

import PropTypes from 'prop-types'
import { uniq } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import { titleize } from 'inflection'

import { orderedRoomPrefixes } from '~/src/Harvest/utils'
import createLogger from '~/src/Lib/Logging'
import { datePropType, idPropType } from '~/src/Lib/PropTypes'
import { EMPTY_OBJECT, getDateTime } from '~/src/Lib/Utils'

import Base from './Base'

const displayName = 'Annotations/Harvest'
const logger = createLogger(displayName)

const dateFormat = { month: 'numeric', day: 'numeric' }

export const formatHarvest = dataToFormat => {
  const { annotation, date, entities = EMPTY_OBJECT } = dataToFormat
  const { facilityCultivars: cultivars = EMPTY_OBJECT, harvests, rooms } = entities
  const harvest = annotation.harvestId && harvests ? harvests[annotation.harvestId] : annotation
  const { name } = annotation
  if (!harvest) return {}
  const { name: harvestName, cultivarIds, harvestDate } = harvest
  const timestamp = getDateTime(date)
  const isFuture = timestamp > getDateTime('now')
  const isHarvested = timestamp.hasSame(getDateTime(harvestDate), 'day')
  const isHarvestPhase = annotation?.uuid?.toLowerCase()?.includes('phase')
  let dateLabel = `start${isFuture ? 's' : 'ed'}${annotation.harvestId ? ` ${name}` : ''}`
  if (isHarvested) {
    dateLabel = isFuture ? 'planned harvest' : 'harvested'
  }
  const roomIds = uniq(orderedRoomPrefixes.map(prefix => harvest[`${prefix}Room`]).filter(Boolean))
  let location
  if (roomIds.length === 1) {
    const { 0: roomId } = roomIds
    location = rooms?.[roomId]?.name
  } else if (roomIds.length > 1) {
    location = orderedRoomPrefixes.reduce((acc, prefix) => (
      harvest[`${prefix}Room`] && rooms?.[harvest[`${prefix}Room`]]
        ? {
          ...acc,
          [titleize(prefix)]: rooms[harvest[`${prefix}Room`]]?.name
        }
        : acc
    ), {})
  }
  const cultivarNames = cultivarIds
    ?.map?.(id => {
      const cultivar = cultivars[id]
      if (!cultivar) {
        return null
      }
      return cultivar.name
    })
    .filter(Boolean)

  const getAnnotatedTitle = () => {
    if (isHarvestPhase) {
      const isFirstPhase = annotation?.id === harvest?.phases?.[0]
      return `Harvest ${harvestName} ${isFirstPhase ? 'started' : 'transitioned to'} ${annotation.name} phase`
    }
    const isHarvestStartDate = timestamp.hasSame(getDateTime(annotation.startDate), 'day')
    return `Harvest ${harvestName} ${isHarvestStartDate ? 'started' : 'ended'}`
  }

  return {
    title: getAnnotatedTitle(),
    cultivarNames,
    date,
    dateFormat,
    dateLabel,
    location,
  }
}

export const HarvestFormatter = props => {
  const { entities } = useConnect('selectEntities')
  const { title, cultivarNames, date, dateLabel } = formatHarvest({ ...props, entities })

  return (
    <Base
      {...props}
      title={title}
      body={cultivarNames?.length ? <small>cultivars: {cultivarNames.join(', ')}</small> : null}
      date={date}
      dateFormat="TINY_DATE"
      dateLabel={dateLabel}
    />
  )
}

HarvestFormatter.propTypes = {
  annotation: PropTypes.shape({
    name: PropTypes.string,
    cultivarIds: PropTypes.arrayOf(PropTypes.number),
    harvestDate: datePropType,
    harvestId: idPropType,
  }).isRequired,
  date: datePropType.isRequired,
}

HarvestFormatter.displayName = displayName

export default React.memo(HarvestFormatter)
